import FunctionHelper from '../../../../Core/helper/FunctionHelper.js';
import { FileMIMEType } from '../Utils.js';
import ConsumerBase from './ConsumerBase.js';

/**
 * @module Grid/feature/export/consumer/WebSocketConsumer
 */

/**
 * This consumer establishes a websocket connection to the backend and sends generated
 * pages one by one to keep client memory footprint low
 *
 * @extends Grid/feature/export/consumer/ConsumerBase
 * @internal
 */
export default class WebSocketConsumer extends ConsumerBase {
    async start(config) {
        const
            me = this,
            [
                connection,
                connectionReadyPromise
            ]  = await me.createWebSocketConnection(config);

        me.message = {
            orientation : config.orientation,
            format      : config.paperFormat,
            fileFormat  : config.fileFormat,
            clientURL   : config.clientURL,
            fileName    : config.fileName
        };

        me.connection = connection;

        me.sendAsBinary = config.sendAsBinary;

        await connectionReadyPromise;

        const executor = (resolve, reject) => {
            const
                onClose = event => {
                    if (event.code !== 1000) {
                        reject(event);
                    }
                },
                cleanupConnection = (code, reason) => {
                    me.connection.removeEventListener('close', onClose);
                    me.connection.close(code, reason);
                    me.connection = null;
                };

            connection.addEventListener('message', message => {
                let result;

                if (message.data instanceof Blob) {
                    result = message.data;
                }
                else {
                    const response = JSON.parse(message.data);

                    if (response.success) {
                        result = response.url;
                    }
                }

                if (result != null) {
                    clearTimeout(timeout);
                    resolve(result);
                }
            });

            connection.addEventListener('close', onClose);

            const timeout = setTimeout(() => {
                cleanupConnection(4000, 'Timeout while waiting for response from server');
                reject('Timeout while waiting for response from server');
            }, config.webSocketRequestTimeout || this.webSocketRequestTimeout);

            config.abortController.signal.addEventListener('abort', () => {
                cleanupConnection(1000, 'Aborted by user');
                clearTimeout(timeout);
            });
        };

        me.messagesListenerAbortablePromiseResult = FunctionHelper.abortablePromise(executor, config.abortController);
    }

    async consumePage(page) {
        this.connection.send(JSON.stringify({
            html : page,
            ...this.message
        }));
    }

    async end() {
        this.connection.send(JSON.stringify({
            done         : true,
            sendAsBinary : this.sendAsBinary
        }));
    }

    async finalize(config) {
        const
            me       = this,
            response = await me.messagesListenerAbortablePromiseResult.promise;

        me.connection.close();

        me.connection = null;

        if (!me.messagesListenerAbortablePromiseResult.controller.signal.aborted && me.openAfterExport) {
            let fileUrl;

            if (typeof response === 'string') {
                fileUrl = response;
            }
            else {
                fileUrl = URL.createObjectURL(response.slice(0, response.size, FileMIMEType[config.fileFormat]));
            }

            me.feature.downloadLink(config.fileName, fileUrl);

            URL.revokeObjectURL(fileUrl);
        }

        me.messagesListenerAbortablePromiseResult = null;
        me.message = null;

        return { result : response };
    }

    async createWebSocketConnection(config) {
        const connection = new WebSocket(config.exportServer.replace(/^http/, 'ws'));

        const executor = (resolve, reject, signal) => {
            const onError = error => {
                if (error.target?.readyState === WebSocket.CLOSED) {
                    reject(new Error('Connection closed or cannot be opened'));
                }
                else {
                    reject(error);
                }
            };

            connection.addEventListener('open', () => {
                connection.removeEventListener('error', onError);
                resolve(connection);
            });

            connection.addEventListener('error', onError);
        };

        const abortablePromiseResult = FunctionHelper.abortablePromise(executor, config.abortController);

        await abortablePromiseResult.promise;

        return [connection, abortablePromiseResult.promise, abortablePromiseResult.controller];
    }
}
